import { PageMetadata, RequestStatus } from 'features/common/types'
import { useEffect, useMemo, useState } from 'react'

interface InfiniteScrollPaginationProps<T> {
  requestStatus: RequestStatus
  remoteList: T[]
  metadata: PageMetadata | undefined
  numElementsPerPage?: number
  remotePageSize?: number
}

const NUM_ELEMENTS_PER_PAGE = 12
const REMOTE_PAGE_SIZE = 96

function useInfiniteScrollPagination<T>({
  metadata,
  remoteList,
  requestStatus,
  numElementsPerPage = NUM_ELEMENTS_PER_PAGE,
  remotePageSize = REMOTE_PAGE_SIZE
}: InfiniteScrollPaginationProps<T>) {
  const [isScrolling, setIsScrolling] = useState<boolean>(false)
  const [bufferedElements, setBufferedElements] = useState<T[]>([])
  const [localPageNumber, setLocalPageNumber] = useState(1)
  const hasMoreElementsOnRemote = !!(metadata && metadata?.total > bufferedElements.length)

  useEffect(() => {
    if (requestStatus !== RequestStatus.Fulfilled) return

    setIsScrolling(false)
    setBufferedElements((prevBuffered: T[]) => {
      if (metadata?.current === 1) {
        clearPagination()
        return remoteList
      }
      return [...prevBuffered, ...remoteList]
    })
  }, [metadata, remoteList, requestStatus])

  const hasToRequestNextPage = useMemo(() => {
    const numberOfLocalPagesByRequest = remotePageSize / numElementsPerPage
    // The condition localPageNumber % numberOfLocalPagesByRequest === 0 doesn't work well if remotePageSize is not a multiple of numElementsPerPage
    return hasMoreElementsOnRemote && localPageNumber % numberOfLocalPagesByRequest === 0
  }, [hasMoreElementsOnRemote, localPageNumber, remotePageSize, numElementsPerPage])

  const localPage = useMemo(() => {
    return bufferedElements.slice(0, localPageNumber * numElementsPerPage)
  }, [bufferedElements, localPageNumber, numElementsPerPage])

  const hasMoreElementsBuffered = useMemo(
    () => localPage.length < bufferedElements.length,
    [localPage, bufferedElements]
  )
  const hasMoreElements = hasMoreElementsBuffered || hasMoreElementsOnRemote

  const nextPage = () => {
    setLocalPageNumber(localPageNumber + 1)
    setIsScrolling(true)
  }

  const clearPagination = () => {
    setLocalPageNumber(1)
  }

  return {
    isScrolling,
    localPage,
    hasToRequestNextPage,
    hasMoreElements,
    hasMoreElementsBuffered,
    hasMoreElementsOnRemote,
    nextPage
  }
}

export default useInfiniteScrollPagination
